import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { permissionsApi, type EffectivePermissions } from '../api/services';
import { PERMISSION_DENIED_EVENT, PRINCIPAL_CHANGED_EVENT } from '../api/client';
import { isAdminAccount, type PermissionKey } from '../lib/permissions';

// ---------------------------------------------------------------------------
// What the signed-in user's tenant permissions allow: role defaults plus what
// an admin set on the Permissions screen, from GET /api/permissions/me.
// Loaded once per signed-in user, then refreshed when:
//   - an EA switches the executive they act for (as EntitlementsContext does),
//   - the window regains focus and the copy is more than 5 minutes old,
//   - the server refuses something (permission_denied) our copy allowed.
//
// It hides nav entries, pages and manage controls the user can't use. It
// never restricts an admin or a platform admin, and "unknown" (not loaded
// yet, or the request failed) restricts nothing: the server enforces every
// permission regardless.
// ---------------------------------------------------------------------------

const STALE_AFTER_MS = 5 * 60_000;
const DENIED_REFRESH_MIN_MS = 30_000;

/** 'loading' until the first answer for this user; 'unknown' when nothing is restricted. */
export type PermissionsStatus = 'unknown' | 'loading' | 'ready';

export interface PermissionsValue {
  status: PermissionsStatus;
  /** Show-by-default: false only when the server has said this user may not. */
  can: (permission: PermissionKey) => boolean;
  refresh: () => Promise<void>;
}

function buildValue(
  status: PermissionsStatus,
  data: EffectivePermissions | null,
  refresh: () => Promise<void>,
): PermissionsValue {
  return {
    status,
    can: (p) => status !== 'ready' || !data || data[p] !== false,
    refresh,
  };
}

const noopRefresh = async () => { /* no provider */ };

/** What a component sees with no provider above it: nothing is restricted. */
export const UNRESTRICTED_PERMISSIONS: PermissionsValue = buildValue('unknown', null, noopRefresh);

const PermissionsContext = createContext<PermissionsValue | null>(null);

export function usePermissions(): PermissionsValue {
  return useContext(PermissionsContext) ?? UNRESTRICTED_PERMISSIONS;
}

/** "CanViewReports" (the 403 body's spelling) to "canViewReports". */
function permissionKey(name: string | undefined): PermissionKey | undefined {
  return name ? (name.charAt(0).toLowerCase() + name.slice(1)) as PermissionKey : undefined;
}

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  // Admins are never restricted here, so there's nothing to load for them.
  const userId = isAuthenticated && user && !isAdminAccount(user.role, user.isTenantAdmin) ? user.id : null;

  // The last answer and whose it is: an answer for someone else (or none yet)
  // reads as 'loading', with no gap between a sign-in and the fetch starting.
  const [answer, setAnswer] = useState<{ userId: string; data: EffectivePermissions | null } | null>(null);

  const fetchedAtRef = useRef(0);
  const requestRef = useRef(0);
  const userIdRef = useRef(userId);
  const answerRef = useRef(answer);
  useEffect(() => { userIdRef.current = userId; answerRef.current = answer; });

  const load = useCallback(async () => {
    const forUser = userIdRef.current;
    if (!forUser) return;
    const request = ++requestRef.current;
    fetchedAtRef.current = Date.now();
    let data: EffectivePermissions | null = null;
    try {
      data = await permissionsApi.getMyPermissions();
    } catch {
      // Restrict nothing: the server still refuses what isn't allowed.
    }
    if (request !== requestRef.current || forUser !== userIdRef.current) return;
    setAnswer({ userId: forUser, data: data && typeof data === 'object' ? data : null });
  }, []);

  // (Re)load for each signed-in user.
  useEffect(() => {
    requestRef.current++;
    fetchedAtRef.current = 0;
    if (userId) void load();
  }, [userId, load]);

  // An EA switched executive.
  useEffect(() => {
    if (!userId) return;
    const onPrincipalChanged = () => { void load(); };
    window.addEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);
    return () => window.removeEventListener(PRINCIPAL_CHANGED_EVENT, onPrincipalChanged);
  }, [userId, load]);

  // Back to the tab after a while: an admin may have changed them meanwhile.
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - fetchedAtRef.current > STALE_AFTER_MS) void load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [userId, load]);

  // Refused something our copy allowed: the copy is stale. A refusal we
  // already expected changes nothing. Throttled either way.
  useEffect(() => {
    if (!userId) return;
    const onDenied = (e: Event) => {
      const key = permissionKey((e as CustomEvent<{ permission?: string }>).detail?.permission);
      if (key && answerRef.current?.data?.[key] === false) return;
      if (Date.now() - fetchedAtRef.current > DENIED_REFRESH_MIN_MS) void load();
    };
    window.addEventListener(PERMISSION_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onDenied);
  }, [userId, load]);

  const current = answer && answer.userId === userId ? answer : null;
  const status: PermissionsStatus = !userId ? 'unknown' : !current ? 'loading' : current.data ? 'ready' : 'unknown';
  const data = current?.data ?? null;
  const value = useMemo(
    () => (userId ? buildValue(status, data, load) : UNRESTRICTED_PERMISSIONS),
    [userId, status, data, load],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/** A fixed answer, for tests and previews (null = unknown). */
export function StaticPermissionsProvider({
  value,
  children,
}: {
  value: Partial<EffectivePermissions> | null;
  children: React.ReactNode;
}) {
  const ctx = useMemo(
    () => (value ? buildValue('ready', value as EffectivePermissions, noopRefresh) : UNRESTRICTED_PERMISSIONS),
    [value],
  );
  return <PermissionsContext.Provider value={ctx}>{children}</PermissionsContext.Provider>;
}
